import { useState, useRef, useEffect, useCallback } from "react";
import { CheckmarkIcon, ChevronDownIcon, SearchIcon, CloseIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  description?: string;
  // Visually grayed out — the option is still toggleable (so users can
  // deselect a stale value) but the styling indicates it is not currently
  // applicable given other active filters.
  disabled?: boolean;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
}

export function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder = "Valitse...",
  searchPlaceholder = "Hae...",
  disabled = false,
  className,
  loading = false,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOptions = options.filter((o) => value.includes(o.value));

  const filtered = options
    .filter(
      (o) =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        o.description?.toLowerCase().includes(search.toLowerCase()),
    )
    // Push disabled (dimmed) options to the bottom of the list so the
    // currently-applicable choices stay close to the search input. Sort is
    // stable in modern engines, so original order is preserved within each
    // group.
    .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled));

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus the search input when the dropdown opens
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const toggleOption = useCallback(
    (optionValue: string) => {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    },
    [value, onChange],
  );

  const clearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange([]);
    },
    [onChange],
  );

  // "Select all / Deselect all" toggle. Operates on visible, non-disabled
  // options (so search-filtered + dimmed items are excluded). When all such
  // options are already selected, the toggle becomes a deselect that only
  // removes those visible-clickable values — any disabled-but-selected
  // entries are preserved (transient state, normally pruned by the parent).
  const visibleClickableValues = filtered
    .filter((o) => !o.disabled)
    .map((o) => o.value);
  const allVisibleClickableSelected =
    visibleClickableValues.length > 0 &&
    visibleClickableValues.every((v) => value.includes(v));

  const toggleAll = useCallback(() => {
    if (visibleClickableValues.length === 0) return;
    if (allVisibleClickableSelected) {
      const visibleSet = new Set(visibleClickableValues);
      onChange(value.filter((v) => !visibleSet.has(v)));
    } else {
      onChange(Array.from(new Set([...value, ...visibleClickableValues])));
    }
  }, [visibleClickableValues, allVisibleClickableSelected, value, onChange]);

  // Build trigger label
  let triggerLabel: string;
  if (loading) {
    triggerLabel = "Ladataan...";
  } else if (selectedOptions.length === 0) {
    triggerLabel = placeholder;
  } else if (selectedOptions.length === 1) {
    triggerLabel = selectedOptions[0].label;
  } else {
    triggerLabel = `${selectedOptions.length} valittu`;
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled || loading}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background",
          "focus:outline-none focus:ring-1 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          selectedOptions.length === 0 && "text-muted-foreground",
        )}
      >
        <span className="truncate">{triggerLabel}</span>
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {selectedOptions.length > 0 && (
            <span
              role="button"
              tabIndex={-1}
              onClick={clearAll}
              className="rounded-sm p-0.5 hover:bg-accent"
            >
              <CloseIcon className="h-3 w-3 opacity-70" />
            </span>
          )}
          <ChevronDownIcon className="h-4 w-4 opacity-50" />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          {/* Search input */}
          <div className="flex items-center border-b px-3">
            <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              // text-base below md: see the note in input.tsx — sub-16px fields
              // trigger iOS Safari's focus zoom.
              className="flex h-9 w-full bg-transparent py-2 text-base md:text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* Select all / Deselect all toggle */}
          {visibleClickableValues.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              className="flex w-full items-center justify-between border-b px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <span>
                {allVisibleClickableSelected ? "Poista valinnat" : "Valitse kaikki"}
              </span>
            </button>
          )}

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Ei tuloksia
              </p>
            ) : (
              filtered.map((option) => {
                const isSelected = value.includes(option.value);
                const isDimmed = option.disabled === true;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={isDimmed}
                    onClick={() => toggleOption(option.value)}
                    className={cn(
                      "flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground",
                      isSelected && "bg-accent/50",
                      isDimmed && "opacity-50 cursor-not-allowed hover:bg-transparent hover:text-inherit",
                    )}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/30",
                      )}
                    >
                      {isSelected && <CheckmarkIcon className="h-3 w-3" />}
                    </div>
                    <div className="text-left">
                      <div>{option.label}</div>
                      {option.description && (
                        <div className="text-xs text-muted-foreground">
                          {option.description}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
